import {
  Drawer,
  Grid,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Typography,
  TextField,
  FormControlLabel,
  Card,
  Button,
  MenuItem,
  Autocomplete,
  Alert,
  IconButton,
  Stack,
  Paper,
  Chip,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { alpha } from '@mui/material/styles';

// ---- Icons (aligned with CommandeFrnGroupeView) ----
import DiamondIcon from '@mui/icons-material/Diamond';
import AddIcon from '@mui/icons-material/Add';
import FeatherIcon from 'feather-icons-react';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TagIcon from '@mui/icons-material/Tag';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// ---- Redux Slices ----
import { getPublicListCatalogueDiamant } from '../../redux/slices/catalogue-diamant.slice';
import { listSettings } from '../../redux/slices/catalogue.slice';
import { createArrivageFB, set_error } from '../../redux/slices/arrivage-fb.slice';
import { filterStockDiaFb } from '../../redux/slices/stock-diamant-fb.slice';
import ResultModal from '../../components/modal/ResultModal';

/* ==================================================================
 * Presentation Constants & Micro Components
 * ================================================================== */
const MONO = "'Inter', 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif";
const ACCENT = '#159073';

const MiniChip = ({ color, icon, label, variant = 'soft' }) => (
  <Box
    title={typeof label === 'string' ? label : undefined}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      height: 22,
      px: 0.85,
      borderRadius: 1,
      bgcolor: variant === 'soft' ? alpha(color, 0.14) : color,
      color: variant === 'soft' ? color : '#fff',
      fontSize: 11,
      fontWeight: 700,
      maxWidth: '100%',
      minWidth: 0,
      border: `1px solid ${alpha(color, 0.25)}`,
      '& svg': { fontSize: 13, flexShrink: 0 },
    }}
  >
    {icon}
    <Box
      component="span"
      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {label}
    </Box>
  </Box>
);

const SectionHeader = ({ icon, title, count, subtitle, action }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{
      py: 1.25,
      px: 2,
      mb: 2,
      borderRadius: 2,
      bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(ACCENT, 0.14),
          color: ACCENT,
          '& svg': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {title}
          </Typography>
          {count !== undefined && count !== null && (
            <Chip
              size="small"
              label={count}
              sx={{
                height: 20,
                fontSize: '0.72rem',
                fontWeight: 800,
                bgcolor: alpha(ACCENT, 0.14),
                color: ACCENT,
              }}
            />
          )}
        </Stack>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {action}
  </Stack>
);

/* ==================================================================
 * Main Component: CmdFournisseurFacture
 * ================================================================== */
const CmdFournisseurFacture = ({ commandes, show, onClose, onUpdateMultiStatus }) => {
  const [f_articles, set_f_articles] = useState([]);
  const [f_metaux, set_f_metaux] = useState([]);
  const [f_pierres, set_f_pierres] = useState([]);
  const [f_fournisseur, set_f_fournisseur] = useState(null);
  const [f_prix_total, set_f_prix_total] = useState(0.0);
  const [is_sur_mesure, set_is_sur_mesure] = useState(false);
  const [sel_sm_pierre, set_sel_sm_pierre] = useState(null);

  const [lst_cata_diamant, set_lst_cata_diamant] = useState([]);
  const [lst_setting, set_lst_setting] = useState([]);
  const [show_success_modal, set_show_success_modal] = useState(false);
  const [result_message, set_result_message] = useState('');
  const [num_facture_fb, set_num_facture_fb] = useState('');

  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.arrivage_fb_store);

  const init_data = () => {
    console.log('-------------------------------------init facture------------------------------');
    let tmp_articles = [];
    let tmp_metaux = [];
    let tmp_pierres = [];

    if (Array.isArray(commandes)) {
      commandes.forEach((cmd, index) => {
        tmp_articles.push({
          taille: cmd?.taille,
          gravure: cmd?.gravure,
          poids_gr: 0.0,
          is_sans_pc: false,
          poids_cts: 0.0,
          poids_pc_cts: 0.0,
          poids_pv_cts: 0.0,
          couleur: cmd?.catalogue?.couleur_or,
          catalogue: cmd?.catalogue,
          prix_facon: 0.0,
          prix_pierres: 0.0,
          prix_facture: 0.0,
          note_fournisseur: null,
          idCmdFournisseur: cmd?.idCmdFournisseur,
          refCmdFournisseur: cmd?.ref,
          ref:
            tmp_articles.length + 1 > 99
              ? `${tmp_articles.length + 1}`
              : tmp_articles.length + 1 > 9
                ? `0${tmp_articles.length + 1}`
                : `00${tmp_articles.length + 1}`,
        });

        tmp_metaux.push(cmd?.catalogue?.type_metal);
        console.log(lst_cata_diamant);
        if (Array.isArray(cmd?.catalogue?.pierres)) {
          let cq_pierre = cmd?.catalogue?.pierres?.map((el, index_2) => {
            let pierre = lst_cata_diamant.find(
              (p) => p?.idCatalogueDia == el?.catalogue_diamant?.idCatalogueDia,
            );

            if (pierre) {
              return {
                id: +tmp_pierres.length + +index_2 + 1,
                idCatalogueDia: pierre?.idCatalogueDia,
                idCmdFournisseur: cmd?.idCmdFournisseur,
                refCmdFournisseur: cmd?.ref,
                sku: pierre?.sku,
                is_provenance_fournisseur: false,
                ref_pierre: null,
                num_gia: null,
                famille: pierre?.famille,
                poids_total_cts: 0.0,
                prix_cts: Number.isFinite(+pierre?.poids_cts)
                  ? +Number(+pierre.prix_unite / +pierre.poids_cts).toFixed(2)
                  : 0.0,
                prix_total: 0.0,
              };
            }
          });
          tmp_pierres = tmp_pierres.concat(cq_pierre.filter(Boolean));
        }
      });

      tmp_metaux = [...new Set(tmp_metaux)].map((el) => ({
        type_metal: el,
        poids_total: 0.0,
        label: lst_setting
          .find((el) => el?.property == 'metaux')
          ?.options?.find((op) => op?.value == el)?.label,
      }));

      console.log(tmp_articles);
      console.log(tmp_metaux);
      console.log(tmp_pierres);

      set_f_articles(tmp_articles);
      set_f_metaux(tmp_metaux);
      set_f_pierres(tmp_pierres);
      set_is_sur_mesure(commandes.filter((el) => el?.catalogue?.is_sur_mesure).length > 0);
      console.log(
        '-------------------------------------end init facture------------------------------',
      );
    }
  };

  const onAddSMPierre = () => {
    if (sel_sm_pierre && Array.isArray(f_pierres))
      set_f_pierres([
        {
          id: +f_pierres.length + 1,
          idCatalogueDia: sel_sm_pierre?.idCatalogueDia,
          idCmdFournisseur: commandes?.[0]?.idCmdFournisseur,
          refCmdFournisseur: commandes?.[0]?.ref,
          sku: sel_sm_pierre?.sku,
          is_provenance_fournisseur: false,
          ref_pierre: null,
          num_gia: null,
          famille: sel_sm_pierre?.famille,
          poids_total_cts: 1,
          prix_cts: Number.isFinite(+sel_sm_pierre?.poids_cts)
            ? +Number(+sel_sm_pierre.prix_unite / +sel_sm_pierre.poids_cts).toFixed(2)
            : 0.0,
          prix_total: Number.isFinite(+sel_sm_pierre?.poids_cts)
            ? +Number(+sel_sm_pierre.prix_unite / +sel_sm_pierre.poids_cts).toFixed(2)
            : 0.0,
        },
        ...f_pierres,
      ]);
  };

  const onDeleteSMPierre = (id) => {
    if (Array.isArray(f_pierres)) set_f_pierres(f_pierres.filter((el) => el?.id != id));
  };

  const load_ressource = () => {
    dispatch(listSettings())
      .unwrap()
      .then((res_sett) => {
        console.log(res_sett);
        set_lst_setting(res_sett);
        dispatch(getPublicListCatalogueDiamant())
          .unwrap()
          .then((res_diamant) => {
            console.log(res_diamant);
            if (Array.isArray(res_diamant)) set_lst_cata_diamant(res_diamant);
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log(err));
  };

  const onConfirm = () => {
    let ids = commandes.map((el) => el?.idCmdFournisseur);
    let idFournisseur = commandes[0]?.fournisseur?.idFournisseur;

    if (Array.isArray(f_articles) && Array.isArray(f_metaux) && Array.isArray(f_pierres)) {
      let unvalid_pierres = f_pierres.filter(
        (el) =>
          (+el?.poids_total_cts <= 0 &&
            el.famille == 'centrale' &&
            !f_articles?.find((art) => art?.idCmdFournisseur == el?.idCmdFournisseur)
              ?.is_sans_pc) ||
          (+el?.poids_total_cts <= 0 && el.famille == 'pavage') ||
          (el?.is_provenance_fournisseur &&
            +el?.prix_total <= 0 &&
            el.famille == 'centrale' &&
            !f_articles?.find((art) => art?.idCmdFournisseur == el?.idCmdFournisseur)
              ?.is_sans_pc) ||
          (el?.is_provenance_fournisseur && +el?.prix_total <= 0 && el.famille == 'pavage') ||
          (!el?.is_provenance_fournisseur &&
            !el?.ref_pierre &&
            el.famille == 'centrale' &&
            !f_articles?.find((art) => art?.idCmdFournisseur == el?.idCmdFournisseur)
              ?.is_sans_pc) ||
          (!el?.is_provenance_fournisseur && !el?.ref_pierre && el.famille == 'pavage'),
      );
      console.log('-----unvalid_pierres');
      console.log(unvalid_pierres);
      if (unvalid_pierres?.length > 0) {
        console.log(unvalid_pierres);
        dispatch(set_error('Vérifier les pierres SVP !'));
        return;
      }
      if (!commandes[0]?.fournisseur?.idFournisseur) {
        dispatch(set_error('Aucune fournisseur trouvés pour cette commande !'));
        return;
      }

      let unvalid_articles = f_articles.filter(
        (el) => +el?.prix_facture <= 0 || +el?.poids_gr <= 0,
      );
      if (unvalid_articles?.length > 0) {
        dispatch(set_error('Vérifier les articles SVP !'));
        return;
      }

      if (!num_facture_fb) {
        dispatch(set_error('Num de facture ext manquant !'));
        return;
      }

      let facture = {
        prix_total_ttc: f_prix_total,
        pierres: f_pierres,
        fournisseur: { idFournisseur: idFournisseur },
        num_facture_fb,
        articles: f_articles.map((el) => ({
          ...el,
          catalogue: { idCatalogue: el?.catalogue?.idCatalogue },
          commande: { idCmdFournisseur: el?.idCmdFournisseur },
        })),
      };

      dispatch(createArrivageFB(facture))
        .unwrap()
        .then((res) => {
          console.log(res);
          onUpdateMultiStatus(ids, 'envoye-fabricant');
          set_result_message(
            `Envoie des commandes effectuées avec succès , Num arrivage : ${res?.ref}`,
          );
          set_show_success_modal(true);
        })
        .catch((err) => console.log(err));
    }
  };

  const onCloseSuccessModal = () => {
    set_show_success_modal(false);
    set_result_message('');
    onClose();
  };

  useEffect(() => {
    init_data();
  }, [commandes]);

  useEffect(() => {
    load_ressource();
  }, []);

  const supplierName = commandes?.[0]?.fournisseur?.nom ?? 'Fournisseur';

  return (
    <Drawer
      anchor="right"
      open={show}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100%',
          bgcolor: '#34373e',
          backgroundImage: 'none !important',
          scrollbarColor: `${alpha('#67c1d3', 0.4)} transparent`,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: alpha('#67c1d3', 0.4), borderRadius: 3 },
        },
      }}
    >
      {/* Sticky Header Bar */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
          bgcolor: (t) => alpha(t.palette.background.paper, 0.9),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(ACCENT, 0.15),
              color: ACCENT,
            }}
          >
            <ReceiptOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="text.primary">
              Envoi & facturation des commandes
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} mt={0.25}>
              <MiniChip color="#0ea5e9" icon={<LocalShippingOutlinedIcon />} label={supplierName} />
              <MiniChip color={ACCENT} label={`${commandes?.length ?? 0} commande(s)`} />
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="contained"
            color="success"
            startIcon={<SendIcon />}
            onClick={onConfirm}
            disabled={loading}
            sx={{
              fontWeight: 500,
              px: 3,
              borderRadius: 1,
              boxShadow: `0 0px 8px ${alpha('#22c55e', 0.35)}`,
            }}
          >
            Confirmer l'envoi
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            sx={{ fontWeight: 500, borderRadius: 1 }}
          >
            Fermer
          </Button>
        </Stack>
      </Paper>

      <Box p={3} sx={{ maxWidth: 1600, mx: 'auto', width: '100%', boxSizing: 'border-box' }}>
        {error && (
          <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Invoice Meta Bar */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2.5,
            bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} lg={3}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block' }}
              >
                N° Facture Fournisseur *
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Ex: FACT-2026-089"
                value={num_facture_fb}
                onChange={(e) => set_num_facture_fb(e?.target?.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReceiptOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: MONO,
                    fontWeight: 700,
                    borderRadius: 1.5,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={8} lg={9}>
              <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" gap={1}>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha('#3b82f6', 0.08),
                    border: `1px solid ${alpha('#3b82f6', 0.2)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Total Articles
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="#3b82f6"
                    sx={{ fontFamily: MONO }}
                  >
                    {f_articles.length}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha('#06b6d4', 0.08),
                    border: `1px solid ${alpha('#06b6d4', 0.2)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Total Pierres
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    color="#06b6d4"
                    sx={{ fontFamily: MONO }}
                  >
                    {f_pierres.length}
                  </Typography>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Section 1: Articles */}
        <SectionHeader
          icon={<Inventory2OutlinedIcon />}
          title="Articles à facturer"
          count={f_articles.length}
          subtitle="Saisie des poids physiques, prix facture et notes du fabricant"
        />
        <Box sx={{ mb: 4 }}>
          <FactureArticleTable
            f_articles={f_articles}
            set_f_articles={set_f_articles}
            lst_setting={lst_setting}
            f_pierres={f_pierres}
          />
        </Box>

        {/* Section 2: Pierres */}
        <SectionHeader
          icon={<DiamondOutlinedIcon />}
          title="Pierres utilisées"
          count={f_pierres.length}
          subtitle="Association des pierres centrales & pavages avec le stock ou provenance fournisseur"
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                size="small"
                sx={{ width: 280 }}
                options={Array.isArray(lst_cata_diamant) ? lst_cata_diamant : []}
                getOptionLabel={(el) =>
                  el ? `${el?.sku} (${el?.type_pierre} | ${el?.famille})` : ''
                }
                value={sel_sm_pierre || null}
                onChange={(event, newValue) => set_sel_sm_pierre(newValue)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Rechercher SKU pierre sur mesure..." />
                )}
              />
              <Button
                variant="contained"
                color="success"
                onClick={onAddSMPierre}
                size="small"
                startIcon={<AddIcon />}
                sx={{ fontWeight: 500, borderRadius: 1 }}
              >
                Ajouter
              </Button>
            </Stack>
          }
        />
        <Box sx={{ mb: 4 }}>
          <FacturePierresTable
            f_pierres={f_pierres}
            set_f_pierres={set_f_pierres}
            commandes={commandes}
            lst_cata_diamant={lst_cata_diamant}
            is_sur_mesure={is_sur_mesure}
            onDeleteSMPierre={onDeleteSMPierre}
            f_articles={f_articles}
          />
        </Box>

        <ResultModal
          show={show_success_modal}
          onClose={onCloseSuccessModal}
          message={result_message}
        />
      </Box>
    </Drawer>
  );
};

/* ==================================================================
 * Subcomponent: FactureArticleTable (Card-based Row Layout)
 * ================================================================== */
const FactureArticleTable = ({ f_articles, set_f_articles, lst_setting, f_pierres }) => {
  const [lst_couleur_metal, set_lst_couleur_metal] = useState([]);

  const onUpdateItemValue = (ref, field, value) => {
    if (field == 'prix_facture') {
      set_f_articles(
        f_articles.map((el) =>
          el?.ref == ref
            ? {
                ...el,
                [field]: value,
                prix_facon:
                  +value > +el?.prix_pierres ? +Number(+value - +el?.prix_pierres).toFixed(2) : 0.0,
              }
            : el,
        ),
      );
    } else
      set_f_articles(f_articles.map((el) => (el?.ref == ref ? { ...el, [field]: value } : el)));
  };

  useEffect(() => {
    if (Array.isArray(lst_setting))
      set_lst_couleur_metal(
        Array.isArray(lst_setting.find((el) => el?.property == 'couleur_or')?.options)
          ? lst_setting
              .find((el) => el?.property == 'couleur_or')
              ?.options?.map((el) => ({ value: el, label: el }))
          : [],
      );
  }, [lst_setting]);

  useEffect(() => {
    if (Array.isArray(f_pierres) && Array.isArray(f_articles) && f_articles?.length > 0) {
      let tmp = [...f_articles];
      tmp.forEach((el) => {
        let poids_pv_cts = f_pierres
          .filter((p) => p?.refCmdFournisseur == el?.refCmdFournisseur && p?.famille == 'pavage')
          .map((p) => p?.poids_total_cts)
          .reduce((acc, curr) => +acc + +curr, 0);
        let poids_pc_cts = f_pierres
          .filter((p) => p?.refCmdFournisseur == el?.refCmdFournisseur && p?.famille == 'centrale')
          .map((p) => p?.poids_total_cts)
          .reduce((acc, curr) => +acc + +curr, 0);
        el.poids_cts = +Number(+poids_pc_cts + +poids_pv_cts).toFixed(3);
        el.poids_pc_cts = +Number(+poids_pc_cts).toFixed(3);
        el.poids_pv_cts = +Number(+poids_pv_cts).toFixed(3);
        el.prix_pierres = f_pierres
          .filter(
            (p) =>
              p?.refCmdFournisseur == el?.refCmdFournisseur && p?.is_provenance_fournisseur == true,
          )
          .reduce((acc, curr) => +Number(+acc + +curr?.prix_total).toFixed(2), 0.0);

        el.prix_facon =
          +el?.prix_facture > el?.prix_pierres
            ? +Number(+el?.prix_facture - +el?.prix_pierres)
            : 0.0;
      });
      console.log(tmp);
      set_f_articles(tmp);
    }
  }, [f_pierres]);

  return (
    <Stack spacing={2}>
      {Array.isArray(f_articles) &&
        f_articles.map((el) => {
          const isErrorGr = +el?.poids_gr <= 0;
          const isErrorFacture = +el?.prix_facture <= 0;

          return (
            <Paper
              key={`art-${el?.ref}`}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (t) => t.palette.background.paper,
                transition: 'border-color .15s',
                '&:hover': { borderColor: alpha(ACCENT, 0.4) },
              }}
            >
              {/* Row Header */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1.5}
                flexWrap="wrap"
                gap={1}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MiniChip
                    color="#ef4444"
                    icon={<ShoppingBagOutlinedIcon />}
                    label={el?.refCmdFournisseur}
                  />
                  <MiniChip
                    color="#0ea5e9"
                    icon={<QrCode2Icon />}
                    label={el?.catalogue?.sku ? `${el?.catalogue?.sku}` : 'SKU'}
                  />
                  {el?.catalogue?.sku_interne && (
                    <MiniChip
                      color="#6366f1"
                      icon={<TagIcon />}
                      label={el?.catalogue?.sku_interne}
                    />
                  )}
                  {el?.catalogue?.is_diamant && (
                    <MiniChip color="#06b6d4" icon={<DiamondIcon />} label="Diamant" />
                  )}
                  {el?.catalogue?.is_sur_mesure && (
                    <MiniChip color="#f59e0b" icon={<ContentCutIcon />} label="Sur mesure" />
                  )}
                </Stack>
                <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                  {el?.catalogue?.nom ?? el?.catalogue?.designation ?? 'Article sans désignation'}
                </Typography>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* Form Inputs */}
                <Grid item xs={12} lg={6}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Poids physique (GR) *"
                        type="number"
                        error={isErrorGr}
                        value={el?.poids_gr}
                        onChange={(e) => onUpdateItemValue(el?.ref, 'poids_gr', e?.target?.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">g</InputAdornment>,
                        }}
                        sx={{ '& input': { fontFamily: MONO, fontWeight: 700 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Prix Facture (€) *"
                        type="number"
                        error={isErrorFacture}
                        value={el?.prix_facture}
                        onChange={(e) =>
                          onUpdateItemValue(el?.ref, 'prix_facture', e?.target?.value)
                        }
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        sx={{ '& input': { fontFamily: MONO, fontWeight: 700 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Note fournisseur"
                        type="text"
                        value={el?.note_fournisseur ?? ''}
                        onChange={(e) =>
                          onUpdateItemValue(el?.ref, 'note_fournisseur', e?.target?.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(ACCENT, el?.is_sans_pc ? 0.1 : 0.03),
                          border: `1px solid ${alpha(ACCENT, el?.is_sans_pc ? 0.3 : 0.1)}`,
                          display: 'inline-flex',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              color="primary"
                              checked={el?.is_sans_pc ?? false}
                              onChange={(e) =>
                                onUpdateItemValue(el?.ref, 'is_sans_pc', e?.target?.checked)
                              }
                            />
                          }
                          label={
                            <Typography fontWeight={700} variant="caption">
                              Sans Pierre Centrale (Sans PC)
                            </Typography>
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Read-only / Computed Spec Fields */}
                <Grid item xs={12} lg={6}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: (t) =>
                        t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Grid container spacing={1.25}>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Taille"
                          value={el?.taille ?? '—'}
                          disabled
                          sx={{ '& input': { fontFamily: MONO } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Gravure"
                          value={el?.gravure ?? '—'}
                          disabled
                          sx={{ '& input': { fontFamily: MONO } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Couleur"
                          value={el?.couleur ?? ''}
                          disabled
                        >
                          <MenuItem value="">Aucune</MenuItem>
                          {Array.isArray(lst_couleur_metal) &&
                            lst_couleur_metal.map((opt) => (
                              <MenuItem key={opt?.value} value={opt?.value}>
                                {opt?.label}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Poids (CTS)"
                          value={el?.poids_cts ?? 0}
                          disabled
                          sx={{ '& input': { fontFamily: MONO, fontWeight: 700 } }}
                        />
                      </Grid>

                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Poids PC (CTS)"
                          value={el?.poids_pc_cts ?? 0}
                          disabled
                          sx={{ '& input': { fontFamily: MONO } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Poids PV (CTS)"
                          value={el?.poids_pv_cts ?? 0}
                          disabled
                          sx={{ '& input': { fontFamily: MONO } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Prix Façon (€)"
                          value={el?.prix_facon ?? 0}
                          disabled
                          sx={{ '& input': { fontFamily: MONO, color: 'success.main' } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Prix Pierres (€)"
                          value={el?.prix_pierres ?? 0}
                          disabled
                          sx={{ '& input': { fontFamily: MONO, color: 'info.main' } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          );
        })}
    </Stack>
  );
};

/* ==================================================================
 * Subcomponent: FactureMetauxTable (Retained for completeness)
 * ================================================================== */
const FactureMetauxTable = ({ f_metaux, set_f_metaux }) => {
  const onUpdateItemValue = (id, field, value) => {
    set_f_metaux(f_metaux.map((el) => (el?.id == id ? { ...el, [field]: value } : el)));
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.04) }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Métal</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Poids utilisés (Gr)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(f_metaux) &&
            f_metaux.map((el) => (
              <TableRow key={`metal-${el?.type_metal}`}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {el?.label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={el?.poids_total}
                    onChange={(e) => onUpdateItemValue(el?.id, 'poids_total', e?.target?.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                    sx={{ width: 180, '& input': { fontFamily: MONO } }}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

/* ==================================================================
 * Subcomponent: FacturePierresTable & FacturePierreItem
 * ================================================================== */
const FacturePierresTable = ({
  f_pierres,
  set_f_pierres,
  commandes,
  lst_cata_diamant,
  is_sur_mesure,
  onDeleteSMPierre,
  f_articles,
}) => {
  const onUpdateItemValue = (id, field, value) => {
    if (field == 'poids_total_cts')
      set_f_pierres(
        f_pierres.map((el) =>
          el?.id == id
            ? { ...el, [field]: value, prix_total: +Number(+value * +el?.prix_cts).toFixed(2) }
            : el,
        ),
      );
    else if (field == 'prix_total')
      set_f_pierres(
        f_pierres.map((el) =>
          el?.id == id
            ? {
                ...el,
                [field]: value,
                prix_cts: +Number(+value / (+el?.poids_total_cts || 1)).toFixed(2),
              }
            : el,
        ),
      );
    else if (field == 'prix_cts')
      set_f_pierres(
        f_pierres.map((el) =>
          el?.id == id
            ? {
                ...el,
                [field]: value,
                prix_total: +Number(+el?.poids_total_cts * +value).toFixed(2),
              }
            : el,
        ),
      );
    else if (field == 'is_provenance_fournisseur' && value == true)
      set_f_pierres(
        f_pierres.map((el) => (el?.id == id ? { ...el, [field]: value, ref_pierre: '' } : el)),
      );
    else if (field == 'ref_pierre') {
      if (value?.ref_pierre)
        set_f_pierres(
          f_pierres.map((el) =>
            el?.id == id
              ? {
                  ...el,
                  ref_pierre: value?.ref_pierre,
                  num_gia: value?.num_gia,
                  prix_cts: +value?.prix_cts,
                  prix_total: +Number(+el?.poids_total_cts * +value?.prix_cts).toFixed(2),
                }
              : el,
          ),
        );
      else
        set_f_pierres(
          f_pierres.map((el) =>
            el?.id == id
              ? {
                  ...el,
                  ref_pierre: value?.ref_pierre,
                  num_gia: value?.num_gia,
                }
              : el,
          ),
        );
    } else set_f_pierres(f_pierres.map((el) => (el?.id == id ? { ...el, [field]: value } : el)));
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead sx={{ bgcolor: (t) => alpha(t.palette.text.primary, 0.04) }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, py: 1.25 }}>Commande</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25 }}>Sku</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25, textAlign: 'center' }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25, width: 140 }}>Poids (CTS)</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25, textAlign: 'center' }}>
              Provenance
            </TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25, minWidth: 220 }}>
              Réf Pierre (Stock)
            </TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25, width: 140 }}>Prix CTS (€)</TableCell>
            <TableCell sx={{ fontWeight: 700, py: 1.25, width: 140 }}>Prix Total (€)</TableCell>
            {is_sur_mesure && <TableCell sx={{ width: 50 }}></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(f_pierres) &&
            f_pierres.map((el, index) => (
              <FacturePierreItem
                item={el}
                onUpdateItemValue={onUpdateItemValue}
                commandes={commandes}
                f_pierres={f_pierres}
                key={`pierre-${index}`}
                lst_cata_diamant={lst_cata_diamant}
                is_sur_mesure={is_sur_mesure}
                onDeleteSMPierre={onDeleteSMPierre}
                is_sans_pc={
                  f_articles?.find((i) => el?.refCmdFournisseur == i?.refCmdFournisseur)?.is_sans_pc
                }
              />
            ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const FacturePierreItem = ({
  onUpdateItemValue,
  item,
  commandes,
  f_pierres,
  lst_cata_diamant,
  is_sur_mesure,
  onDeleteSMPierre,
  is_sans_pc,
}) => {
  const dispatch = useDispatch();
  const [lst_stock_search, set_lst_stock_search] = useState([]);
  const { loading, error } = useSelector((state) => state.stock_diamant_fb_store);
  const [fournisseur, set_fournisseur] = useState(null);
  const [error_validation, set_error_validation] = useState('');
  const [key_auto_comp, set_key_auto_comp] = useState('');

  const onSearchStock = (event, value, reason) => {
    console.log(value);
    if (value && String(value).length >= 2)
      dispatch(
        filterStockDiaFb({
          code_inventaire: value,
          status: 'en-depot-fb',
          sku: item?.sku,
          fournisseur,
        }),
      )
        .unwrap()
        .then((res) => {
          set_lst_stock_search(Array.isArray(res) ? res : []);
        })
        .catch((err) => console.log(err));
  };

  const onSetPierreRef = (value) => {
    set_error_validation('');
    console.log(value);
    if (+value?.poids_cts == 0) {
      set_error_validation('Echec ,Le poids du pierre choisie est 0 !');
      set_key_auto_comp(`st-${Date.now()}`);
      return;
    }
    if (!Array.isArray(f_pierres)) {
      set_key_auto_comp(`st-${Date.now()}`);
      return;
    }
    let pierres_found = f_pierres.filter((el) => el?.ref_pierre == value?.code_inventaire);
    let poids_cts_found = pierres_found.reduce(
      (acc, curr) => +acc + +Number(+curr?.poids_total_cts).toFixed(3),
      0.0,
    );
    poids_cts_found += +item?.poids_total_cts;
    console.log('pierres_found');
    console.log(pierres_found);
    console.log(poids_cts_found);
    if (value?.num_gia && pierres_found.length > 0) {
      set_error_validation('Echec ,Vous avez déja choisie cette pierre !');
      set_key_auto_comp(`st-${Date.now()}`);
      return;
    }
    if (+poids_cts_found > +value?.poids_cts) {
      set_error_validation(`Echec ,Le Poids utilisé (${+poids_cts_found}) est > poids dispo  !`);
      set_key_auto_comp(`st-${Date.now()}`);
      return false;
    }

    onUpdateItemValue(item?.id, 'ref_pierre', {
      ref_pierre: value?.code_inventaire,
      num_gia: value?.stock_dia?.num_gia,
      prix_cts: value?.stock_dia?.prix_cts,
    });
    return true;
  };

  useEffect(() => {
    console.log('ref change : ', item?.ref_pierre);
    if (!item?.ref_pierre) set_key_auto_comp(`st-${Date.now()}`);
  }, [item?.ref_pierre]);

  useEffect(() => {
    onUpdateItemValue(item?.id, 'ref_pierre', '');
  }, [item?.poids_total_cts]);

  useEffect(() => {
    if (Array.isArray(commandes) && commandes.length > 0)
      set_fournisseur({ idFournisseur: commandes[0]?.fournisseur?.idFournisseur });
  }, [commandes]);

  const isCentrale = item?.famille === 'centrale';
  const isPoidsError =
    (+item?.poids_total_cts <= 0 && !is_sans_pc && isCentrale) ||
    (+item?.poids_total_cts <= 0 && !isCentrale);

  return (
    <TableRow sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.action.hover, 0.04) } }}>
      <TableCell sx={{ py: 1 }}>
        <MiniChip
          color="#ef4444"
          icon={<ShoppingBagOutlinedIcon />}
          label={item?.refCmdFournisseur}
        />
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <MiniChip color="#0ea5e9" icon={<QrCode2Icon />} label={item?.sku} />
      </TableCell>
      <TableCell sx={{ py: 1, textAlign: 'center' }}>
        <MiniChip
          color={isCentrale ? '#3b82f6' : '#ec4899'}
          icon={isCentrale ? <DiamondIcon /> : <TagIcon />}
          label={item?.famille ?? '—'}
        />
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          error={isPoidsError}
          value={item?.poids_total_cts}
          onChange={(e) => onUpdateItemValue(item?.id, 'poids_total_cts', e?.target?.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">cts</InputAdornment>,
          }}
          sx={{ '& input': { fontFamily: MONO, fontWeight: 700 } }}
        />
      </TableCell>
      <TableCell sx={{ py: 1, textAlign: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="primary"
              checked={item?.is_provenance_fournisseur ?? false}
              onChange={(e) =>
                onUpdateItemValue(item?.id, 'is_provenance_fournisseur', e?.target?.checked)
              }
            />
          }
          label={
            <Typography variant="caption" fontWeight={700}>
              {item?.is_provenance_fournisseur ? 'Fabricant' : 'Isor'}
            </Typography>
          }
        />
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <Autocomplete
          key={key_auto_comp}
          disabled={item?.is_provenance_fournisseur}
          options={Array.isArray(lst_stock_search) ? lst_stock_search : []}
          getOptionLabel={(el) =>
            el?.code_inventaire ? `${el?.code_inventaire} (${el?.poids_cts} cts)` : ''
          }
          loading={loading}
          onInputChange={onSearchStock}
          onChange={(event, newValue) => onSetPierreRef(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              error={error_validation !== ''}
              helperText={error_validation}
              placeholder="Code Inventaire..."
              sx={{ '& input': { fontFamily: MONO } }}
            />
          )}
        />
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          disabled={!item?.is_provenance_fournisseur}
          value={item?.prix_cts}
          onChange={(e) => onUpdateItemValue(item?.id, 'prix_cts', e?.target?.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          sx={{ '& input': { fontFamily: MONO } }}
        />
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          disabled={!item?.is_provenance_fournisseur}
          value={item?.prix_total}
          onChange={(e) => onUpdateItemValue(item?.id, 'prix_total', e?.target?.value)}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          sx={{ '& input': { fontFamily: MONO, fontWeight: 700 } }}
        />
      </TableCell>
      {is_sur_mesure && (
        <TableCell sx={{ py: 1, textAlign: 'center' }}>
          <IconButton size="small" color="error" onClick={() => onDeleteSMPierre(item?.id)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
};

export default CmdFournisseurFacture;
